import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader, PageLoader } from '@/components/common/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { fmtDate, fmtPct } from '@/lib/format';
import { toast } from 'sonner';
import { canValidate, useAuth } from '@/lib/auth';
import { BeforeAfterViewer } from '@/components/detection/BeforeAfterViewer';
import { ScanSearch } from 'lucide-react';

export default function ObservationDetailPage() {
  const { obsId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [obs, setObs] = useState(null);
  const [baselines, setBaselines] = useState([]);
  const [runOpen, setRunOpen] = useState(false);

  const load = async () => {
    const [o] = await Promise.all([
      api.get(`/observations/${obsId}`, { params: { include_image: true } }).then(r => r.data),
    ]);
    setObs(o);
    const bl = await api.get('/baselines', { params: { asset_id: o.asset_id } }).then(r => r.data);
    setBaselines(bl);
  };
  useEffect(() => { load(); }, [obsId]);

  const approveAsBaseline = async () => {
    // create + approve
    const b = await api.post('/baselines', { asset_id: obs.asset_id, observation_id: obs.id, label: 'Manual baseline' }).then(r => r.data);
    await api.post(`/baselines/${b.id}/approve`);
    toast.success('Baseline approved');
    load();
  };

  if (!obs) return <div className="p-6"><PageLoader /></div>;

  const src = obs.image_b64 ? `data:${obs.image_mime || 'image/jpeg'};base64,${obs.image_b64}` : null;
  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <PageHeader
        title={`${obs.data_source} · ${obs.sensor}`}
        subtitle={`Acquired ${fmtDate(obs.acquired_at)}`}
        actions={
          <div className="flex items-center gap-2">
            {canValidate(user.role) && (
              <>
                <Button variant="outline" onClick={approveAsBaseline} data-testid="approve-baseline-btn">Approve as baseline</Button>
                <Dialog open={runOpen} onOpenChange={setRunOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="run-detection-btn"><ScanSearch size={14} className="mr-1" /> Run detection</Button>
                  </DialogTrigger>
                  <RunDetectionDialog obs={obs} baselines={baselines.filter(b => b.approved)} onClose={() => setRunOpen(false)} />
                </Dialog>
              </>
            )}
          </div>
        }
      />

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-0 lg:col-span-2 overflow-hidden">
          {src ? <img src={src} alt="observation" className="w-full object-cover" /> :
            <div className="p-8 text-center text-muted-foreground">No image data</div>}
        </Card>
        <Card className="p-4">
          <div className="text-sm font-semibold">Metadata</div>
          <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
            <Row k="Source" v={obs.data_source} />
            <Row k="Sensor" v={obs.sensor} />
            <Row k="Acquired" v={fmtDate(obs.acquired_at)} />
            <Row k="Cloud" v={fmtPct(obs.cloud_coverage_pct, 1)} />
            <Row k="Resolution" v={`${obs.spatial_resolution_m} m`} />
            <Row k="Coverage" v={fmtPct(obs.coverage_pct, 0)} />
            <Row k="Quality" v={<Badge variant="outline" className="text-[10px]">{obs.quality}</Badge>} />
            <Row k="Usable" v={obs.usable ? 'Yes' : 'No'} />
            <Row k="Processing" v={<Badge variant="secondary" className="text-[10px]">{obs.processing_status}</Badge>} />
          </div>
          {obs.notes && <div className="mt-3 text-xs text-muted-foreground border-t border-border pt-3">{obs.notes}</div>}
        </Card>
      </div>

      <div className="mt-4">
        <Card className="p-4">
          <div className="text-sm font-semibold mb-3">Baselines for this asset</div>
          {baselines.length === 0 ? <div className="text-sm text-muted-foreground">No baselines</div> :
            <div className="divide-y divide-border">
              {baselines.map(b => (
                <div key={b.id} className="flex items-center gap-3 py-2">
                  <span className="font-mono text-xs">v{b.version}</span>
                  <div className="flex-1 text-sm">{b.label}</div>
                  <Badge variant={b.approved ? 'default' : 'outline'} className="text-[10px]">{b.approved ? 'Approved' : 'Draft'}</Badge>
                  <span className="text-xs text-muted-foreground">{fmtDate(b.created_at)}</span>
                </div>
              ))}
            </div>
          }
        </Card>
      </div>
    </div>
  );
}

function Row({ k, v }) { return (<><div className="text-muted-foreground">{k}</div><div className="text-right font-medium">{v}</div></>); }

function RunDetectionDialog({ obs, baselines, onClose }) {
  const [baseId, setBaseId] = useState(baselines[0]?.observation_id || '');
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/detections', {
        asset_id: obs.asset_id,
        baseline_observation_id: baseId,
        new_observation_id: obs.id,
      });
      toast.success('Detection complete');
      onClose();
      nav(`/app/detections/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Detection failed');
    } finally { setBusy(false); }
  };
  return (
    <DialogContent>
      <DialogHeader><DialogTitle>Run change detection</DialogTitle></DialogHeader>
      <div className="space-y-3 mt-2">
        <div className="text-sm text-muted-foreground">Select an approved baseline to compare against the current observation.</div>
        {baselines.length === 0 ? <div className="text-sm text-red-500">No approved baselines. Approve one first.</div> :
          <Select value={baseId} onValueChange={setBaseId}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {baselines.map(b => <SelectItem key={b.id} value={b.observation_id}>{b.label} (v{b.version})</SelectItem>)}
            </SelectContent>
          </Select>}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={run} disabled={!baseId || busy} data-testid="run-detection-confirm-btn">{busy ? 'Running (≈20s)…' : 'Run detection'}</Button>
      </DialogFooter>
    </DialogContent>
  );
}
